import { createTheme } from '@mui/material/styles'
import type {} from '@mui/x-charts/themeAugmentation'
import {
  ActivityAvatarVariants,
  ActivityColors,
  ActivityPaperVariants,
} from './activityThemeVariants'
import {
  DefaultColors,
  DefaultTheme,
  mobileQuery,
  onActive,
  onDisabled,
  onFocus,
  onHover,
  spacing,
} from './themeConstants'

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'body1'
  | 'body2'
  | 'subtitle1'
  | 'caption'
  | 'button'

/** These overrides allow us to define new Chip variants */
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    success: true
    info: true
    warn: true
    error: true
    critical: true
    nonInteractive: true
    baseSelected: true
    baseUnselected: true
    customColor: true
    inactive: true
  }
  export type ChipVariant = keyof ChipPropsVariantOverrides
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h1: true
    h2: true
    h3: true
    body1: true
    body2: true
    subtitle1: true
    caption: true
    button: true
  }
}

/** These overrides allow us to define new Icon variants */
declare module '@mui/material/Icon' {
  interface IconPropsVariantOverrides {
    green: true
    orange: true
    blue: true
    grey: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    cardButton: true
  }
  interface ButtonOwnProps {
    id: string
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonOwnProps {
    id: string
    'aria-label': string
  }
}

export const colors = {
  ...DefaultColors,
  ...ActivityColors,
} as const

const theme = createTheme({
  breakpoints: {
    values: {
      xs: DefaultTheme.breakpoints.values.xs, // 0
      sm: DefaultTheme.breakpoints.values.sm, // 600
      md: DefaultTheme.breakpoints.values.md, // 900
      lg: DefaultTheme.breakpoints.values.lg, // 1200
      xl: DefaultTheme.breakpoints.values.xl, // 1536
    },
  },
  palette: {
    primary: {
      main: colors.bluePrimary,
      light: colors.blueLight,
      dark: colors.blueDark,
      contrastText: colors.white,
    },
    error: {
      main: colors.redPrimary,
      light: colors.coralPrimary,
      dark: colors.redDark,
      contrastText: colors.white,
    },
  },
  typography: {
    fontFamily: 'Figtree',
    htmlFontSize: 16,
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: colors.grayPrimary,
      marginTop: DefaultTheme.spacing(6),
      [mobileQuery]: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: colors.grayPrimary,
      marginTop: DefaultTheme.spacing(5),
      [mobileQuery]: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
        marginTop: DefaultTheme.spacing(3),
      },
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: colors.grayPrimary,
      marginTop: DefaultTheme.spacing(5),
      [mobileQuery]: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.4,
        marginTop: DefaultTheme.spacing(3),
      },
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      color: colors.grayPrimary,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '1rem',
      fontWeight: 600,
      color: colors.grayPrimary,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1.25rem',
      fontWeight: 'normal',
      color: colors.grayPrimary,
      lineHeight: 1.4,
    },
    caption: {
      fontSize: '0.875rem',
      fontWeight: 'normal',
      color: colors.grayMedium,
      lineHeight: 1.5,
      display: 'block',
    },
    button: {
      textTransform: 'none',
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
  },
  components: {
    MuiLink: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
          textDecoration: 'none',
          [onHover]: {
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          alignItems: 'center',
          [mobileQuery]: {
            alignItems: 'flex-start',
          },
          borderColor: 'transparent',
          '& .MuiAlert-message': {
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: 1.5,
          },
          '& .MuiAlert-action .MuiButton-root': {
            whiteSpace: 'nowrap',
            flexShrink: 0,
          },
        },
        standardSuccess: {
          backgroundColor: colors.greenExtraLight,
          '& .MuiAlert-message': {
            color: `${colors.greenDark} !important`,
          },
          '& .MuiSvgIcon-root': {
            color: colors.greenDark,
          },
          '& .MuiButtonBase-root': {
            '& .MuiSvgIcon-root': {
              color: colors.greenDark,
            },
          },
          '& .MuiAlert-action .MuiIconButton-root': {
            color: 'inherit',
            background: 'inherit',
            fontSize: 'inherit',
          },
        },
        standardInfo: {
          '& .MuiAlert-message': {
            color: `${colors.blueDark} !important`,
          },
          backgroundColor: colors.blueExtraLight,
          '& .MuiSvgIcon-root': {
            color: colors.bluePrimary,
          },
          '& .MuiButtonBase-root': {
            '& .MuiSvgIcon-root': {
              color: colors.bluePrimary,
            },
          },
        },
        standardError: {
          '& .MuiAlert-message': {
            color: `${colors.redDark} !important`,
          },
          backgroundColor: colors.redExtraLight,
          '& .MuiSvgIcon-root': {
            color: colors.redPrimary,
          },
          '& .MuiButtonBase-root': {
            '& .MuiSvgIcon-root': {
              color: colors.redPrimary,
            },
          },
        },
        standardWarning: {
          '& .MuiAlert-message': {
            color: `${colors.orangeDark} !important`,
          },
          backgroundColor: colors.goldExtraLight,

          '& .MuiSvgIcon-root': {
            color: colors.goldDark,
          },
          '& .MuiButtonBase-root': {
            '& .MuiSvgIcon-root': {
              color: colors.goldDark,
            },
          },
        },
      },
    },
    MuiPaper: {
      variants: [...ActivityPaperVariants],
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          borderRadius: DefaultTheme.spacing(2),
          boxShadow: 'none',
          border: `1px solid ${colors.grayUltraLight}`,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: colors.boxShadow,
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          boxShadow: colors.boxShadow,
          '&.MuiPickersPopper-root': {
            borderRadius: spacing(2),
          },
          '&.MuiAutocomplete-popper': {
            borderRadius: spacing(2),
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          fontSize: '1rem',
          lineHeight: '140%',
          borderRadius: 100,
          boxShadow: 'none',
          marginTop: 24,
          gap: 8,
          padding: 0,
          minHeight: 48,
          minWidth: 0,
        },
        sizeLarge: {
          padding: '12px 24px',
          height: 64,
          fontSize: '1.25rem',
        },
        sizeSmall: {
          padding: '0',
        },
        containedPrimary: {
          backgroundColor: colors.bluePrimary,
          padding: '8px 24px',
          [mobileQuery]: {
            padding: '12px 24px',
          },
          color: colors.white,
          '&:hover': {
            backgroundColor: colors.blueDark,
            boxShadow: colors.hoverShadow,
          },
          '&:focus': {
            boxShadow: colors.boxShadow,
          },
          '&:active': {
            backgroundColor: colors.blueMedium,
            boxShadow: 'none',
          },
          '&:disabled': {
            backgroundColor: colors.grayMedium,
            borderColor: colors.grayMedium,
            color: colors.white,
          },
          '.MuiSvgIcon-root': {
            color: colors.white,
          },
        },
        containedSecondary: {
          backgroundColor: colors.redPrimary,
          padding: '8px 24px',
          [mobileQuery]: {
            padding: '12px 24px',
          },
          color: colors.white,
          '&:hover': {
            backgroundColor: colors.redDark,
            boxShadow: colors.hoverShadow,
          },
          '&:focus': {
            boxShadow: colors.boxShadow,
          },
          '&:active': {
            backgroundColor: colors.redPrimary,
            boxShadow: 'none',
          },
          '&:disabled': {
            backgroundColor: colors.grayMedium,
            borderColor: colors.grayMedium,
            color: colors.white,
          },
          '.MuiSvgIcon-root': {
            color: colors.white,
          },
        },
        outlinedPrimary: {
          borderWidth: 2,
          padding: '8px 24px',
          [mobileQuery]: {
            padding: '12px 24px',
          },
          color: colors.grayDark,
          backgroundColor: colors.white,
          borderColor: colors.bluePrimary,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: colors.white,
            borderColor: colors.blueDark,
            boxShadow: colors.hoverShadow,
          },
          '&:focus': {
            borderWidth: 3,
            borderColor: colors.bluePrimary,
            boxShadow: colors.focusShadow,
          },
          '&:active': {
            backgroundColor: colors.white,
            borderColor: colors.bluePrimary,
            boxShadow: 'none',
          },
          '&:disabled': {
            backgroundColor: colors.white,
            color: colors.grayMedium,
            borderColor: colors.grayMedium,
          },
          '.MuiSvgIcon-root': {
            color: colors.bluePrimary,
          },
        },
        outlinedError: {
          borderWidth: 2,
          padding: '8px 24px',
          [mobileQuery]: {
            padding: '12px 24px',
          },
          color: colors.redPrimary,
          backgroundColor: colors.white,
          borderColor: colors.redPrimary,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: colors.white,
            borderColor: colors.redDark,
            boxShadow: colors.hoverShadow,
          },
          '&:focus': {
            borderWidth: 3,
            borderColor: colors.redPrimary,
            boxShadow: colors.focusShadow,
          },
          '&:active': {
            backgroundColor: colors.white,
            borderColor: colors.redPrimary,
            boxShadow: 'none',
          },
          '&:disabled': {
            backgroundColor: colors.white,
            color: colors.grayMedium,
            borderColor: colors.grayMedium,
          },
          '.MuiSvgIcon-root': {
            color: colors.redPrimary,
          },
        },
        text: {
          color: colors.bluePrimary,
          padding: '0 !important', // NOTE: See TextVariantButtonSpacing in globalStyles.ts
          minHeight: 0,
          minWidth: 0,
          borderRadius: 0,
          marginTop: 0,
          '&:hover': {
            padding: 0,
            borderRadius: 0,
            backgroundColor: 'unset',
            boxShadow: 'unset',
            color: colors.blueDark,
          },
          '&:focus': {
            padding: 0,
            borderRadius: 0,
            backgroundColor: 'unset',
            boxShadow: 'unset',
            color: colors.blueDark,
            textDecoration: `underline ${colors.blueLight}`,
          },
          '&:active': {
            padding: 0,
            borderRadius: 0,
            backgroundColor: 'unset',
            boxShadow: 'unset',
            color: colors.bluePrimary,
          },
          '&:disabled': {
            color: colors.grayMedium,
            '.MuiButton-startIcon svg': {
              color: colors.grayMedium,
            },
          },
          '.MuiSvgIcon-root': {
            color: colors.bluePrimary,
          },
        },
        startIcon: {
          marginRight: 0,
        },
        endIcon: {
          margin: 0,
        },
      },
      variants: [
        {
          props: { variant: 'cardButton' },
          style: {
            border: `1px solid ${colors.bluePrimary}`,
            borderRadius: DefaultTheme.spacing(2),
            boxShadow: colors.boxShadow,
            color: colors.grayDark,
            minHeight: 96,
            justifyContent: 'flex-start',
            padding: DefaultTheme.spacing(4),
            [mobileQuery]: {
              padding: DefaultTheme.spacing(3),
              width: 'auto',
            },
            [onHover]: {
              backgroundColor: colors.blueUltraLight,
              border: `1px solid ${colors.blueDark}`,
              boxShadow: colors.hoverShadow,
            },
            [onFocus]: {
              boxShadow: `${colors.boxShadow}, ${colors.boxShadow}`,
              '.MuiButton-startIcon svg': {
                color: colors.grayDark,
              },
            },
            '&:active': {
              backgroundColor: colors.blueUltraLight,
              border: `1px solid ${colors.blueMedium}`,
              boxShadow: colors.boxShadow,
            },
            '&.Mui-disabled, &.Mui-disabled': {
              color: colors.grayMedium,
              border: `1px solid ${colors.grayMedium}`,
              '.MuiTypography-root': {
                color: colors.grayMedium,
              },
            },
            '.MuiTypography-root': {
              marginTop: 0,
              [DefaultTheme.breakpoints.between(600, 700)]: {
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              },
            },
            '.MuiButton-startIcon svg': {
              [mobileQuery]: {
                fontSize: 28,
              },
              fontSize: 32,
            },
          },
        },
      ],
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          color: colors.grayExtraLight,
          borderColor: colors.grayExtraLight,
          '&::before, &::after': {
            borderColor: colors.grayExtraLight,
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '& .MuiSvgIcon-root': {
            color: colors.bluePrimary,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          minHeight: 48,
          '&.Mui-disabled': {
            backgroundColor: colors.grayUltraLight,
            filter: 'none',
            borderColor: colors.grayExtraLight,
          },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            display: 'none',
          },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
        },
        input: {
          '&.Mui-disabled': {
            color: colors.grayMedium,
          },
          '&::placeholder': {
            color: colors.grayPrimary,
            opacity: 1,
          },
        },
        multiline: {
          height: 160,
          overflowY: 'auto',
          display: 'block',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: colors.white,
          border: `1px solid ${colors.grayExtraLight}`,
          '&:hover': {
            border: `1px solid ${colors.blueMedium}`,
          },
          '&:focus': {
            border: `1px solid ${colors.blueDark}`,
            boxShadow: colors.boxShadow,
          },
          '&:active': {
            border: `1px solid ${colors.bluePrimary}`,
          },
          '&.Mui-disabled': {
            border: `1px solid ${colors.grayExtraLight}`,
            backgroundColor: colors.grayUltraLight,
          },
          '&.Mui-error': {
            border: `1px solid ${colors.redPrimary}`,
          },
          maxWidth: 684,
        },
        notchedOutline: {
          border: 'none',
        },
        input: {
          padding: '8px 16px',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        fullWidth: {
          maxWidth: 684,
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiInputLabel: {
      variants: [
        {
          props: { color: 'error' },
          style: {
            color: colors.redPrimary,
          },
        },
      ],
      styleOverrides: {
        root: {
          marginTop: 24,
          maxWidth: 684,
          overflow: 'visible',
          [mobileQuery]: {
            textWrap: 'wrap',
          },
          '&.Mui-disabled': {
            color: colors.grayDark,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          color: colors.grayDark,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            borderBottom: `1px solid ${colors.grayMedium}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 400,
          color: colors.grayDark,
          lineHeight: 1.5,
        },
        head: {
          textWrap: 'nowrap',
          padding: '12px 12px',
          '&:first-of-type': {
            padding: '16px 8px 16px 24px',
          },
          fontWeight: 700,
        },
        body: {
          padding: '12px 12px',
          '&:first-of-type': {
            padding: '16px 8px 16px 24px',
          },
          '&:last-of-type': {
            padding: '16px 24px 16px 8px',
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          '&.Mui-active': {
            fontSize: '1rem',
            fontWeight: 700,
            color: colors.grayDark,
            lineHeight: 1.5,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '& .MuiSvgIcon-root': {
            color: colors.bluePrimary,
          },
          '&.Mui-checked': {
            color: colors.bluePrimary,
            '&:hover, &:focus': {
              color: colors.blueDark,
            },
          },
          '&.Mui-disabled': {
            color: colors.grayMedium,
          },
        },
      },
    },

    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: colors.blueDark,
        },
        fontSizeSmall: {
          fontSize: '1rem',
        },
      },
      variants: [
        {
          props: { color: 'warning' },
          style: {
            color: colors.orangePrimary,
          },
        },
        {
          props: { color: 'success' },
          style: {
            color: colors.greenPrimary,
          },
        },
        {
          props: { color: 'error' },
          style: {
            color: colors.coralDark,
          },
        },
        {
          props: { color: 'disabled' },
          style: {
            color: colors.grayMedium,
          },
        },
      ],
    },
    MuiChip: {
      styleOverrides: {
        root: {
          padding: '4px 4px',
          borderWidth: 1,
          minHeight: 36,
          borderRadius: 18,
          backgroundColor: colors.white,
          '.MuiChip-label': {
            fontSize: '1rem',
            padding: '0 12px',
          },
          '& svg': {
            color: 'inherit !important',
          },
        },
        sizeSmall: {
          padding: 0,
          borderWidth: 1,
          minHeight: 24,
          '.MuiChip-label': {
            fontSize: '0.875rem',
          },
        },
        labelSmall: {
          padding: '0 8px !important',
          fontSize: '0.875rem',
        },
      },
      variants: [
        {
          props: { variant: 'critical' },
          style: {
            color: colors.redDark,
            backgroundColor: colors.redExtraLight,
          },
        },
        {
          props: { variant: 'success' },
          style: {
            color: colors.greenDark,
            backgroundColor: colors.greenExtraLight,
          },
        },
        {
          props: { variant: 'info' },
          style: {
            color: colors.blueDark,
            backgroundColor: colors.blueExtraLight,
          },
        },
        {
          props: { variant: 'warn' },
          style: {
            color: colors.orangeDark,
            backgroundColor: colors.orangeExtraLight,
          },
        },
        {
          props: { variant: 'inactive' },
          style: {
            color: colors.grayDark,
            backgroundColor: colors.grayExtraLight,
          },
        },
        {
          props: { variant: 'error' },
          style: {
            color: colors.redDark,
            backgroundColor: colors.redExtraLight,
          },
        },
        {
          props: { variant: 'baseSelected' },
          style: {
            color: colors.white,
            backgroundColor: colors.bluePrimary,
            border: `1px solid ${colors.grayExtraLight}`,
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: 1.5,
            '&:hover': {
              border: `1px solid ${colors.blueDark}`,
              backgroundColor: colors.blueDark,
            },
            '&:focus': {
              border: `1px solid ${colors.blueDark}`,
              backgroundColor: colors.blueDark,
            },
            '&:active': {
              border: `1px solid ${colors.blueMedium}`,
              backgroundColor: colors.blueMedium,
            },
            '&.Mui-disabled': {
              backgroundColor: colors.grayMedium,
            },
            '& svg': {
              color: `${colors.white} !important`,
            },
          },
        },
        {
          props: { variant: 'baseUnselected' },
          style: {
            color: colors.grayDark,
            backgroundColor: colors.white,
            border: `1px solid ${colors.grayExtraLight}`,
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: 1.5,
            '&:hover': {
              border: `1x solid ${colors.bluePrimary}`,
              backgroundColor: colors.blueExtraLight,
            },
            '&:focus': {
              border: `1px solid ${colors.blueDark}`,
              boxShadow: colors.boxShadow,
            },
            '&:active': {
              border: `1px solid ${colors.blueMedium}`,
              backgroundColor: colors.blueUltraLight,
            },
            '&.Mui-disabled': {
              border: `1px solid ${colors.grayLight}`,
              backgroundColor: colors.grayUltraLight,
            },
          },
        },
        {
          props: { variant: 'nonInteractive' },
          style: {
            cursor: 'default',
            color: colors.grayDark,
            backgroundColor: colors.white,
            border: `1px solid ${colors.bluePrimary}`,
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: 1.5,
          },
        },
        {
          props: { variant: 'customColor' },
          style: {
            borderWidth: 1,
            backgroundColor: colors.white,
            borderStyle: 'solid',
            boxSizing: 'border-box',
          },
        },
      ],
    },
    MuiIcon: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          //thumb - unchecked
          color: colors.white,
          // track - checked
          '&.Mui-checked+.MuiSwitch-track': {
            opacity: 1,
          },
          // thumb - unchecked + disabled
          '&.Mui-disabled': {
            color: colors.grayUltraLight,
          },
          // track - unchecked + disabled
          '&.Mui-disabled+.MuiSwitch-track': {
            opacity: 1,
            backgroundColor: colors.grayMedium,
          },
        },
        colorPrimary: {
          // thumb - checked
          '&.Mui-checked': {
            color: colors.white,
          },
        },
        track: {
          // track - unchecked
          opacity: 1,
          backgroundColor: colors.grayMedium,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: 16,
          color: colors.white,
          backgroundColor: colors.grayDark,
          borderRadius: 8,
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: 1.5,
        },
        popper: {
          boxShadow: 'none !important',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: colors.boxShadow,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colors.blueExtraLight,
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          [mobileQuery]: {
            minHeight: DefaultTheme.spacing(5),
          },
        },
      },
    },
    MuiAvatar: {
      variants: [...ActivityAvatarVariants],
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiAutocomplete-inputRoot': {
            minWidth: 200,
          },
          '& .MuiInputAdornment-root': {
            paddingLeft: DefaultTheme.spacing(1),
          },
          '.MuiSvgIcon-root': {
            color: colors.grayPrimary,
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'primary',
      },
      variants: [
        {
          props: { color: 'primary' },
          style: {
            '& .MuiSvgIcon-root': {
              color: colors.bluePrimary,
            },
            '&.Mui-checked': {
              color: colors.bluePrimary,
              '&:hover, &:focus': {
                color: colors.blueDark,
              },
              '&:active': {
                color: colors.blueMedium,
              },
            },
          },
        },
        {
          props: { color: 'error' },
          style: {
            '& .MuiSvgIcon-root': {
              color: colors.redPrimary,
            },
            '&.Mui-checked': {
              color: colors.redPrimary,
              '&:hover, &:focus': {
                color: colors.redDark,
              },
              '&:active': {
                color: colors.redExtraLight,
              },
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          padding: 0,
          '& .MuiSvgIcon-root': {
            color: colors.bluePrimary,
          },
          '&.Mui-checked': {
            color: colors.bluePrimary,
            '&:hover, &:focus': {
              color: colors.blueDark,
            },
            '&:active': {
              color: colors.blueMedium,
            },
          },
          '&.Mui-disabled': {
            color: colors.grayMedium,
            '& .MuiSvgIcon-root': {
              color: colors.grayMedium,
            },
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          color: colors.bluePrimary,
          '& svg': {
            color: colors.bluePrimary,
          },
          [onHover]: {
            backgroundColor: colors.blueExtraLight,
            color: colors.blueDark,
            boxShadow: colors.hoverShadow,
          },
          [onFocus]: {
            backgroundColor: colors.blueExtraLight,
            color: colors.blueDark,
            boxShadow: colors.boxShadow,
          },
          [onActive]: {
            backgroundColor: colors.blueExtraLight,
            color: colors.blueMedium,
          },
          '& .MuiSvgIcon-root': {
            color: 'inherit',
          },
          [`${onDisabled} .MuiSvgIcon-root`]: {
            color: colors.grayMedium,
          },
          [onDisabled]: {
            backgroundColor: colors.white,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          '& .MuiListItemText-primary': {
            color: colors.grayDark,
          },
          '& .MuiListItemText-secondary': {
            color: colors.grayPrimary,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 30,
          '& .MuiSvgIcon-root': {
            color: colors.grayPrimary,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
        },
        expandIconWrapper: {
          '& .MuiSvgIcon-root': {
            fontSize: 48,
            color: colors.bluePrimary,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          borderRadius: spacing(2),
          boxShadow: 'none',
          border: `1px solid ${colors.grayUltraLight}`,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          marginLeft: 0,
        },
      },
    },
    // MUI charts
    MuiChartsAxis: {
      styleOverrides: {
        root: {
          '& .MuiChartsAxis-line': {
            stroke: colors.grayExtraLight,
          },
          '& .MuiChartsAxis-tickLabel': {
            fill: colors.grayDark,
            fontSize: 14,
          },
          '& .MuiChartsAxis-label text': {
            fill: colors.grayPrimary,
            fontWeight: 700,
            fontSize: '16px !important',
          },
        },
      },
    },
    MuiBarLabel: {
      styleOverrides: {
        root: {
          fill: colors.white,
        },
      },
    },
    MuiChartsTooltip: {
      defaultProps: {
        trigger: 'item',
      },
      styleOverrides: {
        root: {
          backgroundColor: colors.grayDark,
          borderRadius: spacing(1),
        },
        table: {
          backgroundColor: colors.grayDark,
        },
        cell: {
          color: `${colors.white} !important`,
        },
      },
    },
    MuiChartsLegend: {
      styleOverrides: {
        root: {
          '& .MuiChartsLegend-mark': {
            height: 20,
            rx: spacing(2),
            ry: spacing(2),
          },
          '& text': {
            fill: colors.grayPrimary,
            fontWeight: 400,
            fontSize: '16px !important',
          },
        },
      },
    },
    MuiBottomNavigationAction: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          minWidth: 0,
          padding: '0 2px 0 2px',
          '& svg': {
            color: colors.grayPrimary,
          },
          '& .MuiBottomNavigationAction-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            opacity: 1,
            fontSize: '0.75rem !important',
            color: colors.grayPrimary,
          },
          '&.Mui-selected': {
            '& .MuiBottomNavigationAction-label': {
              color: colors.bluePrimary,
            },
            '& svg': {
              color: colors.bluePrimary,
            },
            '& .MuiBadge-root svg, & svg.MuiSvgIcon-root': {
              color: `${colors.bluePrimary} !important`,
            },
            '&:active': {
              '& .MuiBottomNavigationAction-label': {
                color: colors.bluePrimary,
              },
              '& svg': {
                color: colors.bluePrimary,
              },
              '& .MuiBadge-root svg, & svg.MuiSvgIcon-root': {
                color: `${colors.bluePrimary} !important`,
              },
            },
          },
        },
      },
    },
  },
})

export default theme
